import React from "react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  confirmButtonColor?: string;
  loading?: boolean;
  showReasonInput?: boolean;
  reasonValue?: string;
  onReasonChange?: (value: string) => void;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText = "Cancel",
  confirmButtonColor = "bg-red-600 hover:bg-red-700",
  loading = false,
  showReasonInput = false,
  reasonValue = "",
  onReasonChange,
  reasonPlaceholder = "Enter reason...",
  reasonRequired = false,
}) => {
  if (!isOpen) return null;

  const isConfirmDisabled =
    loading || (showReasonInput && reasonRequired && !reasonValue.trim());

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>

        {showReasonInput && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason {reasonRequired && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={reasonValue}
              onChange={(e) => onReasonChange?.(e.target.value)}
              placeholder={reasonPlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
            />
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors duration-200"
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`${confirmButtonColor} text-white px-4 py-2 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={isConfirmDisabled}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
